/**
 * Leads list — sticky stage chips above a single list of rows.
 *
 * "הכל" = the open pipeline only (lead / contacted / approved). "אושר להרשמה ונרשם" and
 * "לא רלוונטי" each have their own chip so the default view stays actionable.
 * Each row carries a status dropdown (ProspectStageControl); clicking the row
 * itself opens the lead page via onSelect.
 */

import { useState } from "react";
import { ChatCircleDots, ShieldCheck, WarningCircle } from "@phosphor-icons/react";
import {
  PROSPECT_STAGE_ORDER,
  PROSPECT_STAGE_LABELS,
  PROSPECT_STAGE_COLORS,
  PROSPECT_SOURCE_LABELS,
} from "../../../../services/nivFirebase";
import { formatPhoneIL } from "../../../../utils/phoneUtils";
import { getProfession } from "../../../../data/startPageContent";
import ProspectStageControl from "./ProspectStageControl";
import { AiSummaryPeek, CopyPhoneButton, NotePeek } from "../shared/RowQuickActions";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Stages shown under "הכל" — the ones that still need work. */
export const RELEVANT_STAGES = ["lead", "contacted", "approved"];

/** Hebrew relative date for last activity (updatedAt). */
const formatRelativeDate = (dateString) => {
  if (!dateString) return null;
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.floor((Date.now() - d.getTime()) / MS_PER_DAY);
  if (days < 0) return null;
  if (days === 0) return "היום";
  if (days === 1) return "אתמול";
  if (days < 30) return `לפני ${days} ימים`;
  return d.toLocaleDateString("he-IL", { day: "numeric", month: "short" });
};

function ProspectList({ prospects, unlinkedRequests = [], counts = {}, author, onSelect }) {
  const [stageFilter, setStageFilter] = useState("all");

  const filtered =
    stageFilter === "all"
      ? prospects.filter((p) => RELEVANT_STAGES.includes(p.stage || "lead"))
      : prospects.filter((p) => (p.stage || "lead") === stageFilter);

  const relevantCount = RELEVANT_STAGES.reduce((sum, s) => sum + (counts[s] || 0), 0);

  return (
    <div className="nivcl-list-wrap">
      {/* Stage filter chips */}
      <div className="nivcl-chips" role="tablist" aria-label="סינון לפי שלב">
        <button
          type="button"
          role="tab"
          aria-selected={stageFilter === "all"}
          className={`nivcl-chip ${stageFilter === "all" ? "is-active" : ""}`}
          onClick={() => setStageFilter("all")}
        >
          הכל
          <span className="nivcl-chip-count">{relevantCount}</span>
        </button>
        {PROSPECT_STAGE_ORDER.map((stage) => {
          const color = PROSPECT_STAGE_COLORS[stage];
          const isActive = stageFilter === stage;
          return (
            <button
              key={stage}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`nivcl-chip nivcl-chip-stage ${isActive ? "is-active" : ""}`}
              style={{ "--nivcl-chip-color": color }}
              onClick={() => setStageFilter(stage)}
            >
              <span className="nivcl-stage-dot" style={{ backgroundColor: color }} />
              {PROSPECT_STAGE_LABELS[stage]}
              <span className="nivcl-chip-count">{counts[stage] || 0}</span>
            </button>
          );
        })}
      </div>

      {/* Unlinked call requests — invalid phone, read-only */}
      {unlinkedRequests.length > 0 && (
        <div className="nivcl-unlinked-banner">
          <div className="nivcl-unlinked-title">
            <WarningCircle size={14} weight="fill" />
            פניות עם מספר לא תקין ({unlinkedRequests.length})
          </div>
          {unlinkedRequests.map((req) => (
            <div key={req.id} className="nivcl-unlinked-row">
              <span className="nivcl-row-name">{req.fullName || "ללא שם"}</span>
              <span className="nivcl-row-phone" dir="ltr">
                {req.phone || "—"}
              </span>
              {req.source && (
                <span className="nivcl-row-source">
                  {PROSPECT_SOURCE_LABELS[req.source] || req.source}
                </span>
              )}
              <span className="nivcl-badge-invalid">
                <WarningCircle size={11} weight="fill" />
                מספר לא תקין
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Lead rows */}
      <div className="nivcl-list">
        {filtered.length === 0 && (
          <div className="nivcl-col-empty">
            {stageFilter === "all" ? "אין לידים פתוחים — הכל טופל 🎉" : "אין לידים להצגה"}
          </div>
        )}
        {filtered.map((prospect) => {
          const stage = prospect.stage || "lead";
          const stageColor = PROSPECT_STAGE_COLORS[stage] || "#9ca3af";
          const sourceLabel = PROSPECT_SOURCE_LABELS[prospect.source] || prospect.source;
          const professionLabel = prospect.profession ? getProfession(prospect.profession)?.label || prospect.profession : null;
          const notesCount = (prospect.notes || []).length;
          const lastActivity = formatRelativeDate(prospect.updatedAt || prospect.createdAt);
          const isTerminal = stage === "lost";

          return (
            <div
              key={prospect.id}
              className={`nivcl-row${isTerminal ? " is-terminal" : ""}`}
              style={{ "--nivcl-row-tint": `${stageColor}14` }}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(prospect)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(prospect);
                }
              }}
            >
              <span className="nivcl-stage-dot nivcl-row-dot" style={{ backgroundColor: stageColor }} />

              {/* Right (RTL start): identity */}
              <div className="nivcl-row-identity">
                <span className="nivcl-row-name">
                  {prospect.fullName || formatPhoneIL(prospect.phone || prospect.id)}
                </span>
                <span className="nivcl-row-meta">
                  {prospect.clinicName && <span>{prospect.clinicName}</span>}
                  <span className="nivcl-row-phone-cell">
                    <span dir="ltr">{formatPhoneIL(prospect.phone || prospect.id)}</span>
                    <CopyPhoneButton phone={prospect.phone || prospect.id} />
                  </span>
                  {sourceLabel && <span>{sourceLabel}</span>}
                  {professionLabel && <span className="nivcl-row-profession">{professionLabel}</span>}
                  {lastActivity && <span>{lastActivity}</span>}
                </span>
              </div>

              {/* Middle: hints */}
              <div className="nivcl-row-status">
                {prospect.existsInSystem && !prospect.clinicId && (
                  <span className="nivcl-card-exists" title="מספר זה כבר רשום במערכת">
                    <ShieldCheck size={11} weight="fill" />
                    קיים במערכת
                  </span>
                )}
                <NotePeek text={prospect.freeNotes} updatedAt={prospect.freeNotesUpdatedAt} />
                <AiSummaryPeek aiSummary={prospect.aiSummary} />
                {notesCount > 0 && (
                  <span className="nivcl-card-notes-count">
                    <ChatCircleDots size={11} weight="fill" />
                    {notesCount}
                  </span>
                )}
              </div>

              {/* Left (RTL end): status dropdown */}
              <div className="nivcl-row-actions">
                <ProspectStageControl prospect={prospect} author={author} compact />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ProspectList;
